import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus } from "lucide-react";

const USER_ROLES = ["user", "expert", "influencer", "official", "guest"] as const;

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: CreateUserPayload) => void;
  isSaving?: boolean;
}

export interface CreateUserPayload {
  username: string;
  phone_number: string;
  role: string;
  country: string;
  country_code: string;
  phone_code: string;
  sponsor_code?: string;
  distributor_code?: string;
  sponsor_name?: string;
  referral_code?: string;
}

const EMPTY: CreateUserPayload = {
  username: "",
  phone_number: "",
  role: "user",
  country: "",
  country_code: "",
  phone_code: "",
  sponsor_code: "",
  distributor_code: "",
  sponsor_name: "",
  referral_code: "",
};

export default function CreateUserDialog({ open, onOpenChange, onSave, isSaving }: CreateUserDialogProps) {
  const [form, setForm] = React.useState<CreateUserPayload>(EMPTY);

  React.useEffect(() => {
    if (!open) setForm(EMPTY);
  }, [open]);

  const set = (field: keyof CreateUserPayload) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isValid = form.username.trim() && form.phone_number.trim() && form.country.trim() &&
    form.country_code.trim() && form.phone_code.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    const payload: CreateUserPayload = { ...form };
    // Remove empty optional fields
    (["sponsor_code", "distributor_code", "sponsor_name", "referral_code"] as const).forEach((k) => {
      if (!payload[k]) delete payload[k];
    });
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> Create New User
          </DialogTitle>
          <DialogDescription>
            Fill in the required fields to create a new user account.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-2">
          {/* Required fields */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Required</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username <span className="text-red-500">*</span></Label>
                <Input id="username" value={form.username} onChange={set("username")} placeholder="e.g. john_doe" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number <span className="text-red-500">*</span></Label>
                <Input id="phone_number" value={form.phone_number} onChange={set("phone_number")} placeholder="e.g. 60123456789" />
              </div>
              <div className="space-y-2">
                <Label>Role <span className="text-red-500">*</span></Label>
                <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country <span className="text-red-500">*</span></Label>
                <Input id="country" value={form.country} onChange={set("country")} placeholder="e.g. Malaysia" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country_code">Country Code <span className="text-red-500">*</span></Label>
                <Input id="country_code" value={form.country_code} onChange={set("country_code")} placeholder="e.g. MY" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone_code">Phone Code <span className="text-red-500">*</span></Label>
                <Input id="phone_code" value={form.phone_code} onChange={set("phone_code")} placeholder="e.g. +60" />
              </div>
            </div>
          </div>

          {/* Optional fields */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Optional</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sponsor_name">Sponsor Name</Label>
                <Input id="sponsor_name" value={form.sponsor_name} onChange={set("sponsor_name")} placeholder="Sponsor's name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sponsor_code">Sponsor Code</Label>
                <Input id="sponsor_code" value={form.sponsor_code} onChange={set("sponsor_code")} placeholder="Sponsor code" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="distributor_code">Distributor Code</Label>
                <Input id="distributor_code" value={form.distributor_code} onChange={set("distributor_code")} placeholder="Distributor code" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="referral_code">Referral Code</Label>
                <Input id="referral_code" value={form.referral_code} onChange={set("referral_code")} placeholder="Referral code to apply" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!isValid || isSaving}>
              <UserPlus className="w-4 h-4 mr-2" />
              {isSaving ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
